import { useState } from 'react';
import { Target } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FAB } from '@/components/FormControls';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useApiResource } from '@/hooks/useApiResource';
import { useLang } from '@/context/LanguageContext';
import api from '@/lib/api';
import { GoalCard } from '@/components/goals/GoalCard';
import { GoalFormDrawer } from '@/components/goals/GoalFormDrawer';

const TABS = ['Active', 'Paused', 'Completed'];

const matchesTab = (goal, tab) =>
  tab === 'Active' ? goal.status === 'Active' || goal.status === 'In Progress' : goal.status === tab;

export default function Goals() {
  const { t, tEnum } = useLang();
  const { data, loading, reload } = useApiResource(
    () => api.get('/goals').then((r) => r.data),
    { errorMessage: t('goals.loadError') }
  );
  const goals = data || [];
  const [tab, setTab] = useState('Active');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (goal) => { setEditing(goal); setDrawerOpen(true); };

  const filtered = goals.filter((g) => matchesTab(g, tab));

  if (loading) return <FullScreenLoader label={t('goals.loader')} />;

  return (
    <AppLayout>
      <AppHeader title={t('goals.title')} subtitle={t('goals.subtitle')} />

      <Tabs value={tab} onValueChange={setTab} className="mt-3">
        <TabsList data-testid="goals-tabs" className="grid w-full grid-cols-3 rounded-xl bg-white/[0.06]">
          {TABS.map((tabValue) => (
            <TabsTrigger key={tabValue} value={tabValue}
              className="rounded-lg text-xs data-[state=active]:bg-brand-cyan data-[state=active]:text-brand-ink">
              {tEnum(tabValue)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-4 space-y-3">
        {filtered.length === 0 ? (
          <EmptyState icon={Target} title={t('goals.emptyTitle')} subtitle={t('goals.emptySubtitle')} />
        ) : (
          filtered.map((g, i) => <GoalCard key={g.id} goal={g} index={i} onClick={() => openEdit(g)} />)
        )}
      </div>

      <FAB onClick={openCreate} testId="goals-add-fab" label={t('goals.addFab')} />

      <GoalFormDrawer open={drawerOpen} onOpenChange={setDrawerOpen} goal={editing} onSaved={reload} />
    </AppLayout>
  );
}
